import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { uuid } from 'uuidv4';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import {
  formatMoneyNumeral,
  formatPercentageString,
} from '../../util/helpers/numeralHelpers';
import Note from '../Notes/Note';
import InformationList from '../InformationList/InformationList';
import InformationListItem from '../InformationList/InformationListItem';
import Paragraph from '../Paragraph/Paragraph';
import { selectSettings } from '../../store/selectors/settingsSelectors';

const OrderTotal = ({ finalOrder, orderType, orderValues, buyCashBuffer }) => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const [
    cashOrderInformationListData,
    setCashOrderInformationListData,
  ] = useState([]);
  const [
    quantityOrderInformationListData,
    setQuantityOrderInformationListData,
  ] = useState([]);
  const [
    quantityOrderInformationListDataWithCashBuffer,
    setQuantityOrderInformationListDataWithCashBuffer,
  ] = useState([]);
  const [priceLocked, setPriceLocked] = useState(false);

  useEffect(() => {
    if (!settings || isEmpty(settings) || !settings?.BrokerCode) {
      return;
    }
    setPriceLocked(!!settings?.IsLockedPricingActive);

    const dataForCashOrder = [
      {
        value: formatMoneyNumeral(finalOrder.pricePerUnit),
        label: settings.IsLockedPricingActive
          ? t('product.detailsTable.pricePerUnit')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.pricePerUnit'),
      },
      {
        value: finalOrder.quantity,
        label: settings.IsLockedPricingActive
          ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.quantity')
          : t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.estimatedQuantity',
            ),
      },
      settings?.IsLockedPricingActive && finalOrder?.volumeDiscount > 0
        ? {
            value: formatMoneyNumeral(finalOrder.volumeDiscount),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.volumeDiscount',
            ),
            isBold: true,
          }
        : { value: '', label: '' },
      {
        value: formatMoneyNumeral(finalOrder.estimatedTotal),
        label: settings.IsLockedPricingActive
          ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.total')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.estimatedTotal'),
        isBold: true,
      },
    ];
    setCashOrderInformationListData(dataForCashOrder);

    const dataForQuantityOrder = [
      {
        value: formatMoneyNumeral(finalOrder.pricePerUnit),
        label: settings.IsLockedPricingActive
          ? t('product.detailsTable.pricePerUnit')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.pricePerUnit'),
      },
      {
        value: finalOrder.quantity,
        label: t('buyWizard.buyForDelivery.reviewOrder.orderTotal.quantity'),
      },
      settings?.IsLockedPricingActive && finalOrder?.volumeDiscount > 0
        ? {
            value: formatMoneyNumeral(finalOrder.volumeDiscount),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.volumeDiscount',
            ),
            isBold: true,
          }
        : { label: '', value: '' },
      {
        value: formatMoneyNumeral(finalOrder.estimatedTotal),
        label: settings.IsLockedPricingActive
          ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.total')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.estimatedTotal'),
        isBold: true,
      },
    ];
    setQuantityOrderInformationListData(dataForQuantityOrder);

    const dataForQuantityOrderWithBuffer = [
      {
        value: formatMoneyNumeral(finalOrder.cashBuffer),
        label: t('buyWizard.buyForDelivery.reviewOrder.orderTotal.cashBuffer', {
          cashBuffer: formatPercentageString(buyCashBuffer),
        }),
      },
      {
        value: formatMoneyNumeral(finalOrder.requiredBalanceWithBuffer),
        label: t(
          'buyWizard.buyForDelivery.reviewOrder.orderTotal.requiredBalanceWithBuffer',
        ),
        isBold: true,
      },
    ];
    setQuantityOrderInformationListDataWithCashBuffer(
      dataForQuantityOrderWithBuffer,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyCashBuffer, orderValues, orderType, finalOrder, settings]);

  if (orderType === 'Cash' && !priceLocked) {
    return (
      <>
        <Paragraph marginBottom={8} marginTop={24}>
          <strong>{`${t(
            'buyWizard.buyForDelivery.reviewOrder.cashOrder',
          )} ${formatMoneyNumeral(orderValues.CashAmount)}`}</strong>
        </Paragraph>
        <InformationList noMargin withBackground>
          {cashOrderInformationListData.map(({ value, label, isBold }) => (
            <InformationListItem
              key={uuid()}
              fullWidth
              isBold={isBold}
              value={value}
              label={label}
            />
          ))}
        </InformationList>
        {settings && !isEmpty(settings) && !settings?.IsLockedPricingActive && (
          <Paragraph marginTop={24}>
            <Note text={t('buyWizard.common.note')} />
          </Paragraph>
        )}
      </>
    );
  }

  if (orderType === 'Quantity' || priceLocked) {
    return (
      <>
        <Paragraph marginBottom={8} marginTop={24}>
          <strong>{`${t(
            'buyWizard.buyForDelivery.reviewOrder.quantityOrder',
          )} ${
            finalOrder.quantity > 1
              ? `${finalOrder.quantity} ${t('buyWizard.common.units')}`
              : `${finalOrder.quantity} ${t('buyWizard.common.unit')}`
          }`}</strong>
        </Paragraph>

        <InformationList marginBottom={8} withBackground>
          {quantityOrderInformationListData.map(({ label, value, isBold }) => (
            <InformationListItem
              key={uuid()}
              label={label}
              value={value}
              isBold={isBold}
              fullWidth
            />
          ))}
        </InformationList>
        {!settings?.IsLockedPricingActive && (
          <InformationList noMargin withBackground>
            {quantityOrderInformationListDataWithCashBuffer.map(
              ({ label, value, isBold }) => (
                <InformationListItem
                  key={uuid()}
                  label={label}
                  value={value}
                  isBold={isBold}
                  fullWidth
                />
              ),
            )}
          </InformationList>
        )}
        {settings && !isEmpty(settings) && !settings?.IsLockedPricingActive && (
          <Paragraph isColumn marginTop={24}>
            {/* <Note
              marginBottom={8}
              text={t('buyWizard.common.volatilityNote')}
            /> */}
            <Note text={t('buyWizard.common.note')} />
          </Paragraph>
        )}
      </>
    );
  }

  return null;
};

OrderTotal.propTypes = {
  finalOrder: PropTypes.shape({
    pricePerUnit: PropTypes.number,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estimatedTotal: PropTypes.number,
    cashBuffer: PropTypes.number,
    requiredBalanceWithBuffer: PropTypes.number,
    estimatedPriceForUnit: PropTypes.number,
    estimatedQuantity: PropTypes.number,
    volumeDiscount: PropTypes.number,
    newPricePerUnit: PropTypes.number,
  }),
  orderType: PropTypes.string,
  orderValues: PropTypes.shape({
    CashAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  buyCashBuffer: PropTypes.number,
};

export default React.memo(OrderTotal);
